import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Menu, 
  X, 
  Package, 
  TrendingUp, 
  Egg, 
  FileText,
  Home,
  Calendar,
  Bell,
  DollarSign
} from 'lucide-react';
import { designTokens } from '../design/designTokens';
import TopRightProfile from './TopRightProfile';

const SidebarContainer = styled.div<{ collapsed: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: ${props => props.collapsed ? '80px' : '320px'};
  background: ${designTokens.colors.surface.primary};
  border-right: 2px solid ${designTokens.riverPoultry.brandColors.green};
  transition: ${designTokens.transitions.normal};
  z-index: ${designTokens.zIndex.docked};
  overflow: hidden;
  box-shadow: ${designTokens.shadows.lg};
  
  /* Mobile: Hide sidebar by default, show as overlay */
  ${designTokens.mediaQueries.maxMd} {
    width: ${props => props.collapsed ? '0' : '320px'};
    transform: ${props => props.collapsed ? 'translateX(-100%)' : 'translateX(0)'};
    z-index: ${designTokens.zIndex.modal};
  }
`;

const SidebarHeader = styled.div`
  padding: ${designTokens.spacing[4]};
  border-bottom: 2px solid ${designTokens.riverPoultry.brandColors.green};
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[4]};
  min-height: 140px;
  background: ${designTokens.colors.surface.primary};
  position: relative;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[4]};
  min-width: 0;
`;

const LogoContainer = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: ${designTokens.borderRadius['2xl']};
  padding: ${designTokens.spacing[2]};
  flex-shrink: 0;
  
  /* Mobile responsive sizing */
  ${designTokens.mediaQueries.maxSm} {
    width: 100px;
    height: 100px;
    padding: ${designTokens.spacing[1]};
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LogoText = styled.div<{ collapsed: boolean }>`
  display: ${props => props.collapsed ? 'none' : 'flex'};
  flex-direction: column;
  gap: ${designTokens.spacing[1]};
  min-width: 0;
`;

const LogoMain = styled.h1`
  margin: 0;
  font-size: ${designTokens.typography.fontSize.lg};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.riverPoultry.brandColors.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: ${designTokens.typography.lineHeight.snug};
  letter-spacing: ${designTokens.typography.letterSpacing.normal};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: ${designTokens.transitions.normal};
  
  &:hover {
    color: ${designTokens.riverPoultry.brandColors.green};
    transform: translateY(-1px);
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  top: ${designTokens.spacing[4]};
  right: ${designTokens.spacing[4]};
  background: ${designTokens.colors.surface.elevated};
  border: 2px solid ${designTokens.colors.border.brand};
  cursor: pointer;
  padding: ${designTokens.spacing[2]};
  border-radius: ${designTokens.borderRadius.lg};
  color: ${designTokens.colors.text.brand};
  transition: ${designTokens.transitions.fast};
  box-shadow: ${designTokens.shadows.sm};

  &:hover {
    background: ${designTokens.colors.surface.interactive};
    border-color: ${designTokens.riverPoultry.brandColors.primary};
    color: ${designTokens.riverPoultry.brandColors.primary};
    transform: scale(1.05);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Navigation = styled.nav`
  padding: ${designTokens.spacing[5]} 0;
  flex: 1;
  overflow-y: auto;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
`;

const NavSection = styled.div`
  margin-bottom: ${designTokens.spacing[6]};
`;

const SectionTitle = styled.h3<{ collapsed: boolean }>`
  margin: 0 0 ${designTokens.spacing[3]} 0;
  padding: 0 ${designTokens.spacing[4]};
  font-size: ${designTokens.typography.fontSize.xs};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.riverPoultry.brandColors.green};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: ${props => props.collapsed ? 'none' : 'block'};
  border-bottom: 1px solid ${designTokens.riverPoultry.brandColors.green};
  padding-bottom: ${designTokens.spacing[2]};
`;

const NavItem = styled(Link)<{ active: boolean; collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[3]};
  padding: ${designTokens.spacing[3]} ${designTokens.spacing[4]};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  text-decoration: none;
  font-weight: ${props => props.active ? designTokens.typography.fontWeight.bold : designTokens.typography.fontWeight.medium};
  font-size: ${designTokens.typography.fontSize.sm};
  transition: ${designTokens.transitions.fast};
  position: relative;
  background: ${props => props.active ? `linear-gradient(135deg, ${designTokens.riverPoultry.brandColors.green} 0%, #1a3a0f 100%)` : 'transparent'};
  border-left: ${props => props.active ? `3px solid ${designTokens.riverPoultry.brandColors.red}` : '3px solid transparent'};
  margin: 0 ${designTokens.spacing[2]} ${designTokens.spacing[1]} ${designTokens.spacing[2]};
  border-radius: ${designTokens.borderRadius.lg};
  box-shadow: ${props => props.active ? '0 4px 12px rgba(46, 125, 50, 0.3)' : 'none'};
  min-height: 44px;

  /* Mobile responsive adjustments */
  ${designTokens.mediaQueries.maxSm} {
    padding: ${designTokens.mobileSpacing[3]} ${designTokens.mobileSpacing[4]};
    gap: ${designTokens.mobileSpacing[2]};
    font-size: ${designTokens.mobileTypography.fontSize.xs};
    min-height: 40px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }

  &:hover {
    background: ${props => props.active ? `linear-gradient(135deg, ${designTokens.riverPoultry.brandColors.green} 0%, #1a3a0f 100%)` : `linear-gradient(135deg, ${designTokens.riverPoultry.brandColors.green}20 0%, #1a3a0f20 100%)`};
    color: ${props => props.active ? 'white' : designTokens.riverPoultry.brandColors.green};
    transform: translateX(4px);
    box-shadow: 0 3px 8px rgba(46, 125, 50, 0.25);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NavText = styled.span<{ collapsed: boolean }>`
  display: ${props => props.collapsed ? 'none' : 'block'};
  white-space: nowrap;
  flex: 1;
`;

const MainContent = styled.div<{ sidebarCollapsed: boolean }>`
  margin-left: ${props => props.sidebarCollapsed ? '80px' : '320px'};
  transition: ${designTokens.transitions.normal};
  min-height: 100vh;
  background: ${designTokens.colors.background.secondary};
  
  /* Mobile: Full width when sidebar is hidden */
  ${designTokens.mediaQueries.maxMd} {
    margin-left: 0;
  }
`;

const TopBar = styled.div`
  background: ${designTokens.colors.surface.primary};
  border-bottom: 1px solid ${designTokens.colors.border.light};
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${designTokens.shadows.sm};
  
  /* Mobile: Add hamburger menu button */
  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]} ${designTokens.mobileSpacing[5]};
  }
`;

const TopBarTitle = styled.h1`
  margin: 0;
  font-size: ${designTokens.typography.fontSize['2xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.colors.text.primary};
  font-family: ${designTokens.typography.fontFamily.secondary};
  
  /* Mobile: Smaller title */
  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xl};
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[4]};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${designTokens.spacing[2]};
  border-radius: ${designTokens.borderRadius.lg};
  color: ${designTokens.colors.text.secondary};
  transition: ${designTokens.transitions.fast};

  &:hover {
    background: ${designTokens.colors.background.secondary};
    color: ${designTokens.colors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ContentArea = styled.div`
  padding: ${designTokens.spacing[6]};
`;

interface VerticalSidebarProps {
  children: React.ReactNode;
  pageTitle: string;
}

const VerticalSidebar: React.FC<VerticalSidebarProps> = ({ children, pageTitle }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      section: 'Main',
      items: [
        { path: '/', label: 'Home', icon: Home },
        { path: '/calendar', label: 'Calendar', icon: Calendar },
        { path: '/growth-tracking', label: 'Growth Tracking', icon: TrendingUp },
        { path: '/batch-management', label: 'Batch Management', icon: Package },
        { path: '/feed-management', label: 'Feed Management', icon: Egg },
        { path: '/production-analysis', label: 'Production Analysis', icon: FileText },
        { path: '/financial-analysis', label: 'Financial Analysis', icon: DollarSign },
      ]
    }
  ];

  return (
    <>
      <SidebarContainer collapsed={collapsed}>
        <SidebarHeader>
          <Logo>
            <LogoContainer>
              <LogoImage 
                src="https://www.riverpoultry.com/assets/river_poultry_logo-f3f23791.png" 
                alt="River Poultry Logo"
              />
            </LogoContainer>
            <LogoText collapsed={collapsed}>
              <LogoMain>Senior Manager</LogoMain>
            </LogoText>
          </Logo>
          <ToggleButton onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </ToggleButton>
        </SidebarHeader>

        <Navigation>
          {navigationItems.map((section, sectionIndex) => (
            <NavSection key={sectionIndex}>
              <SectionTitle collapsed={collapsed}>{section.section}</SectionTitle>
              {section.items.map((item, itemIndex) => (
                <NavItem
                  key={itemIndex}
                  to={item.path}
                  active={location.pathname === item.path}
                  collapsed={collapsed}
                >
                  <item.icon />
                  <NavText collapsed={collapsed}>{item.label}</NavText>
                </NavItem>
              ))}
            </NavSection>
          ))}
        </Navigation>
      </SidebarContainer>

      <MainContent sidebarCollapsed={collapsed}>
        <TopBar>
          <TopBarTitle>{pageTitle}</TopBarTitle>
          <TopBarActions>
            <ActionButton>
              <Bell />
            </ActionButton>
            <TopRightProfile />
          </TopBarActions>
        </TopBar>
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </>
  );
};

export default VerticalSidebar;
